import { useState, useContext, useMemo } from 'react';
import {
    Box,
    Grid,
    Card,
    CardHeader,
    CardActions,
    CardContent,
    CardActionArea,
    Avatar,
    IconButton,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { AppContext, SttFormSearch, SttNoResults } from '../../../sporttia/all';
import ProductDialog from './ProductDialog';
import { formatPriceByLocale } from '../../../lib/utils';
import type { Product } from '../../../types/models/Product';
import { sporttiaColors } from '../../../theme/theme';

// Normalize text: remove accents and convert to lowercase
const normalizeText = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

type SalePointProductCardProps = {
    product: Product;
    selectProduct: (product: Product) => void;
    setOpenProduct: (product: Product) => void;
    canManageProducts: boolean;
};

function SalePointProductCard({
    product,
    selectProduct,
    setOpenProduct,
    canManageProducts,
}: SalePointProductCardProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    return (
        <Card>
            <CardActionArea onClick={() => selectProduct(product)}>
                <CardContent>
                    <Typography variant="h5" component="h2">
                        {product.name}
                    </Typography>
                    <Typography color="textSecondary">
                        {product.category}
                    </Typography>
                </CardContent>
            </CardActionArea>

            <CardActions>
                {formatPriceByLocale(
                    product.finalPrice,
                    cxt.sc?.city?.country?.currency,
                )}

                {product.stockageActive && (
                    <Box className="ml-4">
                        <Typography>
                            {t('general.units')}: {product.stock}
                        </Typography>
                    </Box>
                )}

                {canManageProducts && (
                    <IconButton
                        size="small"
                        className="ml-auto"
                        onClick={() => setOpenProduct(product)}
                    >
                        <CreateIcon />
                    </IconButton>
                )}
            </CardActions>
        </Card>
    );
}

type SalePointProductsProps = {
    selectProduct: (product: Product) => void;
    allProducts: Product[];
    onUpdateProduct: (product: Product) => void;
    onDeleteProduct: (product: Product) => void;
    setAllProducts?: (products: Product[]) => void;
    /** Puede crear y editar productos y su stock (`sale_point.modify`). */
    canManageProducts?: boolean;
};

export default function SalePointProducts({
    selectProduct,
    allProducts,
    onUpdateProduct,
    onDeleteProduct,
    canManageProducts = true,
}: SalePointProductsProps) {
    const { t } = useTranslation();

    const [searchingKeyword, setSearchingKeyword] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>();
    const [openProduct, setOpenProduct] = useState<
        Product | Record<string, never>
    >();

    const filteredProducts = useMemo(() => {
        const normalizedKeyword = normalizeText(searchingKeyword);
        return allProducts
            .filter(
                (product) =>
                    product.category === selectedCategory || searchingKeyword,
            )
            .filter((product) =>
                normalizeText(product.name).includes(normalizedKeyword),
            );
    }, [allProducts, selectedCategory, searchingKeyword]);

    const categories = useMemo(() => {
        return allProducts
            .map((product) => product.category || t('Others'))
            .filter((cat): cat is string => cat !== undefined)
            .filter((value, index, self) => self.indexOf(value) === index);
    }, [allProducts, t]);

    return (
        <Box>
            <Box className="mb-6">
                <SttFormSearch
                    defVal={searchingKeyword}
                    onClean={() => setSearchingKeyword('')}
                    onChange={({ value }: any) => setSearchingKeyword(value)}
                />
            </Box>

            <Grid container spacing={3}>
                {selectedCategory || searchingKeyword ? (
                    <>
                        {selectedCategory && (
                            <Grid size={{ lg: 4, xs: 6 }}>
                                <CardActionArea
                                    onClick={() =>
                                        setSelectedCategory(undefined)
                                    }
                                >
                                    <Card>
                                        <CardHeader
                                            avatar={
                                                <Avatar>
                                                    <ArrowBackIcon />
                                                </Avatar>
                                            }
                                            title={t('Back')}
                                        />
                                    </Card>
                                </CardActionArea>
                            </Grid>
                        )}

                        {filteredProducts.length > 0 &&
                            filteredProducts.map((product: any) => (
                                <Grid key={product.id} size={{ xs: 6, lg: 4 }}>
                                    <SalePointProductCard
                                        selectProduct={selectProduct}
                                        setOpenProduct={setOpenProduct}
                                        canManageProducts={canManageProducts}
                                        product={product}
                                    />
                                </Grid>
                            ))}

                        {filteredProducts.length === 0 && <SttNoResults />}
                    </>
                ) : (
                    <>
                        {categories.map((category: any) => (
                            <Grid key={category} size={{ xs: 6, lg: 4 }}>
                                <CardActionArea
                                    onClick={() =>
                                        setSelectedCategory(category)
                                    }
                                >
                                    <Card>
                                        <CardHeader
                                            avatar={
                                                <Avatar
                                                    style={{
                                                        backgroundColor:
                                                            sporttiaColors.info
                                                                .main,
                                                    }}
                                                >
                                                    <CategoryIcon />
                                                </Avatar>
                                            }
                                            title={category}
                                        />
                                    </Card>
                                </CardActionArea>
                            </Grid>
                        ))}

                        {canManageProducts && (
                            <Grid size={{ lg: 4, xs: 6 }}>
                                <CardActionArea
                                    onClick={() => setOpenProduct({})}
                                >
                                    <Card>
                                        <CardHeader
                                            avatar={
                                                <Avatar
                                                    style={{
                                                        backgroundColor:
                                                            sporttiaColors
                                                                .success.main,
                                                    }}
                                                >
                                                    <AddIcon />
                                                </Avatar>
                                            }
                                            title={t('AddProduct')}
                                        />
                                    </Card>
                                </CardActionArea>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>

            <ProductDialog
                product={openProduct}
                onClose={() => setOpenProduct(undefined)}
                onUpdateProduct={onUpdateProduct}
                onDeleteProduct={onDeleteProduct}
            />
        </Box>
    );
}
